// ═══════════════════════════════════════════════════════════════════
//  IncomingCallPopup — real-time popup for the reception PC.
//
//  Listens to /api/incoming-calls/events (SSE). When the mobile app's
//  native call detector POSTs to /api/incoming-calls/detect, the server
//  fans the event out here within milliseconds. Shows a top-right card
//  with patient name, UHID, balance, last visit. Click "Open Profile"
//  to jump to the patient. Auto-hides 60s after the call ends.
//
//  Different from LiveCallPopup.jsx (legacy 5s polling against
//  /voip/live). This one is push-based and tied to mobile detection.
// ═══════════════════════════════════════════════════════════════════

import React, { useEffect, useState, useRef } from 'react';
import { Phone, X, UserPlus, ExternalLink } from 'lucide-react';

const API_BASE = (window.__API_BASE__ || process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

export default function IncomingCallPopup({ onSelectPatient, onRegisterUnknown }) {
  const [active, setActive] = useState(null); // { callLogId, phone, patient, detectedAt, durationSec? }
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const esRef = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const open = () => {
      if (cancelled) return;
      // Token via query param so EventSource (no headers support) still authenticates.
      // Key name must match what saas-auth/login + the axios interceptor write —
      // they use 'dentalToken'. Older 'token'/'saasToken' fallbacks are dead.
      const token = localStorage.getItem('dentalToken') || '';
      // Without a token the server returns 401 and the browser auto-retries
      // every 4s — that's the noisy "401 (Unauthorized) GET /incoming-calls/events"
      // chatter in DevTools when logged out. Skip the connection entirely.
      if (!token) {
        if (!cancelled) setTimeout(open, 30000); // re-check periodically post-login
        return;
      }
      const url = `${API_BASE}/incoming-calls/events?t=${encodeURIComponent(token)}`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.addEventListener('incoming-call', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (dismissedIds.has(data.callLogId)) return;
          setActive(data);
          // Auto-hide guard: even without a "call ended" event, drop the
          // popup after 2 minutes so it doesn't pile up.
          clearTimeout(hideTimer.current);
          hideTimer.current = setTimeout(() => setActive(null), 120000);
        } catch {}
      });

      es.addEventListener('call-ended', (e) => {
        try {
          const data = JSON.parse(e.data);
          setActive((prev) => prev && prev.callLogId === data.callLogId
            ? { ...prev, durationSec: data.durationSec, ended: true, outcome: data.outcome }
            : prev);
          // Hide 60s after end so receptionist has time to log a note.
          clearTimeout(hideTimer.current);
          hideTimer.current = setTimeout(() => setActive(null), 60000);
        } catch {}
      });

      es.onerror = () => {
        // Reconnect after 4s — EventSource auto-reconnects too but we
        // explicitly close on persistent error to avoid zombie streams.
        try { es.close(); } catch {}
        if (!cancelled) setTimeout(open, 4000);
      };
    };

    open();
    return () => {
      cancelled = true;
      try { esRef.current?.close(); } catch {}
      clearTimeout(hideTimer.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    if (active?.callLogId) {
      setDismissedIds((s) => new Set([...s, active.callLogId]));
    }
    setActive(null);
  };

  if (!active) return null;
  const p = active.patient;

  return (
    <div style={popupStyle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={ringStyle(active.ended)}>
          <Phone size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={badgeStyle(active.ended)}>
            {active.ended
              ? `📞 Call ended · ${formatDuration(active.durationSec)}`
              : '📞 Incoming Call (mobile)'}
          </div>
          <div style={nameStyle}>{p ? p.name : 'Unknown caller'}</div>
          <div style={metaStyle}>
            {active.phone}
            {p?.uhid ? ` · ${p.uhid}` : ''}
          </div>
          {p && (p.balance != null || p.lastVisit) && (
            <div style={subMetaStyle}>
              {p.balance != null && <span>Balance: ₹{p.balance}</span>}
              {p.balance != null && p.lastVisit && <span> · </span>}
              {p.lastVisit && <span>Last visit: {new Date(p.lastVisit).toLocaleDateString()}</span>}
            </div>
          )}
          {p?.activeTreatment && (
            <div style={contextLineStyle}>
              <span style={{ marginRight: 4 }}>🦷</span>
              <span>{p.activeTreatment}</span>
            </div>
          )}
          {p?.nextAppointment && (
            <div style={contextLineStyle}>
              <span style={{ marginRight: 4 }}>📅</span>
              <span>{p.nextAppointment}</span>
            </div>
          )}
        </div>
        <button onClick={dismiss} style={closeBtnStyle} aria-label="Dismiss">
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        {p ? (
          <button
            onClick={() => {
              if (onSelectPatient) onSelectPatient(p);
              else window.location.hash = `#patient-${p._id}`;
              dismiss();
            }}
            style={primaryBtnStyle}
          >
            <ExternalLink size={13} /> Open Profile
          </button>
        ) : (
          <button
            onClick={() => {
              if (onRegisterUnknown) onRegisterUnknown(active.phone);
              dismiss();
            }}
            style={primaryBtnStyle}
          >
            <UserPlus size={13} /> Register Patient
          </button>
        )}
      </div>
    </div>
  );
}

function formatDuration(sec) {
  const s = Number(sec) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

const popupStyle = {
  position: 'fixed', top: 80, right: 20, zIndex: 9999,
  background: '#fff', borderRadius: 14, padding: 14,
  boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
  border: '1px solid #e5e9f0',
  width: 340,
  animation: 'oralzy-slide-in 0.25s ease',
};

const ringStyle = (ended) => ({
  width: 36, height: 36, borderRadius: 18,
  background: ended ? '#64748b' : '#10b981',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  animation: ended ? 'none' : 'oralzy-pulse 1.4s ease-in-out infinite',
  flexShrink: 0,
});

const badgeStyle = (ended) => ({
  fontSize: 11,
  color: ended ? '#64748b' : '#10b981',
  fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
});

const nameStyle = {
  fontSize: 15, fontWeight: 700, color: '#0f172a', marginTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const metaStyle = { fontSize: 12, color: '#6b7785', marginTop: 1 };
const subMetaStyle = { fontSize: 11, color: '#94a3b8', marginTop: 4 };
const contextLineStyle = {
  fontSize: 12, color: '#0f172a', fontWeight: 600, marginTop: 4,
  display: 'flex', alignItems: 'center',
};

const closeBtnStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: '#9aa5b1', padding: 4, alignSelf: 'flex-start',
};
const primaryBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center',
  padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: '#F57C1F', color: '#fff', fontWeight: 700, fontSize: 13, flex: 1,
};
